import gql from "graphql-tag";
import {getImbalances, getImbalancesForExport} from "../../graphql/queries";
import {AppsyncService} from "./AppsyncService";
import {oktaAuth} from "../config/OktaConfig";
import {ImbalanceItem, PersistentImbalanceExport} from "../../common/data/types";
import {Imbalance, ImbalanceFilter} from "../../graphql/audit-overview-api/api";

export type ImbalancePage = {
    items: ImbalanceItem[];
    nextToken: string | null;
    totalCount: number | null;
};

// TODO: Remove test statuses — only for verifying the clean status filter
const TEST_STATUSES = ['Open', 'Open', 'Open', 'Adjustment Submitted', 'Processed'];
let testIdx = 0;

function toImbalanceItem(raw: Imbalance): ImbalanceItem {
    const testStatus = TEST_STATUSES[testIdx % TEST_STATUSES.length];
    testIdx++;
    return {
        material: raw.material ?? '',
        size: raw.size ?? '',
        quality: raw.quality ?? '',
        coo: raw.coo ?? '',
        wmsQty: raw.wmsQty ?? 0,
        ciBondedQty: raw.ciBondedQty ?? 0,
        ciNonBondedQty: raw.ciNonBondedQty ?? 0,
        ciTotalQty: raw.ciTotalQty ?? 0,
        imbalance: raw.imbalance ?? 0,
        persistentImbalance: raw.persistentImbalance ?? 0,
        direction: raw.direction ?? '',
        cleanStatus: testStatus,
        flowInformation: testStatus !== 'Open' ? 'test-ref' : (raw.flowInformation ?? ''),
    };
}

class ImbalanceService {

    private appsyncService: AppsyncService;

    constructor() {
        this.appsyncService = new AppsyncService(
            process.env.REACT_APP_APPSYNC_AUDIT_OVERVIEW_ENDPOINT!,
            oktaAuth
        );
    }

    async fetchImbalances(
        month: string,
        material?: string,
        size?: string,
        isPersistent?: boolean,
        filter?: ImbalanceFilter,
        excludeColors?: boolean,
        nextToken?: string,
    ): Promise<ImbalancePage> {
        const result: any = await this.appsyncService.appSyncClient.query({
            query: gql(getImbalances),
            fetchPolicy: 'network-only',
            variables: {
                month,
                material: material || null,
                size: size || null,
                isPersistent: isPersistent ?? null,
                filter: filter ?? null,
                excludeColors: excludeColors ?? null,
                nextToken: nextToken ?? null,
            },
        });

        const connection = result?.data?.getImbalances;
        if (!connection?.items) return {items: [], nextToken: null, totalCount: null};

        const items = connection.items
            .filter((raw: any) => raw != null)
            .map((raw: any) => toImbalanceItem(raw));

        return {items, nextToken: connection.nextToken || null, totalCount: connection.totalCount ?? null};
    }

    async fetchPersistentImbalances(
        month: string,
        filter?: ImbalanceFilter,
        isPersistent?: boolean,
        material?: string,
        size?: string,
        excludeColors?: boolean,
    ): Promise<PersistentImbalanceExport[]> {
        const allItems: PersistentImbalanceExport[] = [];
        let nextToken: string | null = null;

        do {
            const result: any = await this.appsyncService.appSyncClient.query({
                query: gql(getImbalancesForExport),
                fetchPolicy: 'network-only',
                variables: {
                    month,
                    isPersistent: isPersistent ?? null,
                    filter: filter ?? null,
                    excludeColors: excludeColors ?? null,
                    material: material || null,
                    size: size || null,
                    nextToken,
                },
            });

            const connection = result?.data?.getImbalances;
            if (!connection?.items) break;

            connection.items
                .filter((raw: any) => raw != null)
                .forEach((raw: Imbalance) => {
                    allItems.push({
                        material: raw.material ?? '',
                        size: raw.size ?? '',
                        quality: raw.quality ?? '',
                        coo: raw.coo ?? '',
                        imbalance: raw.imbalance ?? 0,
                        direction: raw.direction ?? '',
                        cleanStatus: raw.cleanStatus ?? '',
                        persistent1m: raw.persistent1m ?? 0,
                        persistent2m: raw.persistent2m ?? 0,
                        persistent3m: raw.persistent3m ?? 0,
                        persistent4m: raw.persistent4m ?? 0,
                        persistent6m: raw.persistent6m ?? 0,
                        persistent12m: raw.persistent12m ?? 0,
                    });
                });

            nextToken = connection.nextToken || null;
        } while (nextToken);

        return allItems;
    }
}

export const imbalanceService = new ImbalanceService();
